/**
 * AI Agent API Service
 *
 * Provides API methods for managing AI agents including CRUD operations,
 * activation/deactivation, and testing agent configurations.
 */

import { api, handleApiError } from '../client'
import type {
  Agent,
  AgentDetail,
  CreateAgentRequest,
  UpdateAgentRequest,
  AgentSearchParams,
  AgentTestResult,
  PaginatedResponse,
  ExecuteAgentRequest,
  ExecutionResult,
} from '../types'

export const agentService = {
  // ==================== Query Methods ====================

  /**
   * Gets agents with optional filtering and pagination.
   * Uses query parameters for search, active status, and pagination.
   */
  async getAgents(params?: AgentSearchParams): Promise<PaginatedResponse<Agent>> {
    try {
      const searchParams = new URLSearchParams()

      if (params?.search) searchParams.append('search', params.search)
      if (params?.isActive !== undefined) searchParams.append('isActive', String(params.isActive))
      if (params?.agentType) searchParams.append('agentType', params.agentType)
      if (params?.skip !== undefined) searchParams.append('skip', String(params.skip))
      if (params?.take !== undefined) searchParams.append('take', String(params.take))

      const queryString = searchParams.toString()
      const url = `/v1/agents${queryString ? `?${queryString}` : ''}`

      const response = await api.get<Agent[]>(url)

      // The backend returns an array; wrap it in PaginatedResponse format
      return {
        items: response.data,
        totalCount: response.data.length,
        pageNumber: 1,
        pageSize: params?.take || response.data.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      }
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets only active agents (for dropdowns and selections).
   */
  async getActiveAgents(): Promise<Agent[]> {
    try {
      const response = await api.get<Agent[]>('/v1/agents?isActive=true')
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a single agent by ID.
   */
  async getAgent(id: string): Promise<AgentDetail> {
    try {
      const response = await api.get<AgentDetail>(`/v1/agents/${id}`)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Gets a single agent by slug with optional capabilities.
   */
  async getAgentBySlug(slug: string, includeCapabilities = true): Promise<AgentDetail> {
    try {
      const response = await api.get<AgentDetail>(
        `/v1/agents/${slug}?includeCapabilities=${includeCapabilities}`
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Mutation Methods ====================

  /**
   * Creates a new agent.
   */
  async createAgent(data: CreateAgentRequest): Promise<AgentDetail> {
    try {
      const response = await api.post<AgentDetail>('/v1/agents', data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Updates an existing agent.
   */
  async updateAgent(id: string, data: UpdateAgentRequest): Promise<AgentDetail> {
    try {
      const response = await api.put<AgentDetail>(`/v1/agents/${id}`, data)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deletes an agent.
   */
  async deleteAgent(id: string): Promise<void> {
    try {
      await api.delete(`/v1/agents/${id}`)
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Activation Methods ====================

  /**
   * Activates an agent.
   */
  async activateAgent(id: string): Promise<AgentDetail> {
    try {
      const response = await api.put<AgentDetail>(`/v1/agents/${id}`, { isActive: true })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Deactivates an agent.
   */
  async deactivateAgent(id: string): Promise<AgentDetail> {
    try {
      const response = await api.put<AgentDetail>(`/v1/agents/${id}`, { isActive: false })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Testing Methods ====================

  /**
   * Tests an agent's configuration with a sample prompt.
   */
  async testAgent(id: string, testPrompt: string): Promise<AgentTestResult> {
    try {
      const response = await api.post<AgentTestResult>(`/v1/agents/${id}/test`, {
        prompt: testPrompt,
      })
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  // ==================== Execution Methods ====================

  /**
   * Executes an agent, letting it decide which skill to use based on the user message.
   */
  async executeAgent(id: string, request: ExecuteAgentRequest): Promise<ExecutionResult> {
    try {
      const response = await api.post<ExecutionResult>(`/v1/agents/${id}/execute`, request)
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },

  /**
   * Executes a specific skill for an agent.
   */
  async executeSkill(
    agentId: string,
    skillSlug: string,
    request: ExecuteAgentRequest
  ): Promise<ExecutionResult> {
    try {
      const response = await api.post<ExecutionResult>(
        `/v1/agents/${agentId}/skills/${skillSlug}/execute`,
        request
      )
      return response.data
    } catch (error) {
      throw new Error(handleApiError(error))
    }
  },
}
